import { withDataTransformer } from '@/components/layout/DataTransformer';
import PaginationControls from '@/components/layout/utils/PaginationControls';
import { Link, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import { formatPageTitle } from '../app/utils/meta';
import Layout, { Meta } from '../components/layout/Layout';
import SectionOneCol from '../components/layout/sections/SectionOneCol';
import { getContributorPlaceholderLogo, siteLogoLink } from '../components/layout/utils/Logo';
import withLocation from '../components/withLocation';

interface EditorialTeamTemplatePropsData extends React.HTMLAttributes<HTMLDivElement> {
  editorialTeam: any;
  guestEditors: any;
  site: any;
}

interface EditorialTeamTemplateProps extends React.HTMLAttributes<HTMLDivElement> {
  data: EditorialTeamTemplatePropsData;
}

/**
 * Renders out editors page
 * @param {any} data
 * @return {JSX}
 */
const EditorialTeamTemplate = ({ data }: EditorialTeamTemplateProps) => {
  const editorialTeamData = data.editorialTeam.nodes;
  const guestTeamData = data.guestEditors.nodes;
  const [editorialTeam, setEditorialTeam] = useState({
    currentPage: 0
  });
  const [guestTeam, setGuestTeam] = useState({
    currentPage: 0
  });
  const meta: Meta[] = [
    {
      tag: 'meta',
      content: formatPageTitle(`Editorial team`),
      href: '',
      name: 'title',
      rel: ''
    }
  ];

  const getContributors = (teamData: any) => {
    return (
      <div className="grid grid-cols-1 divide-y">
        {teamData?.map((editor: any) => {
          if (typeof window === 'undefined') {
            return null;
          }
          const ShowFirstParagraph = (htmlString: string): string | null => {
            // Create a DOM parser to parse the HTML string
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlString, 'text/html');
            const paragraphs = doc.querySelectorAll('p');

            // Add a class 'hidden' to all subsequent <p> tags as we only want to see 1st para but good for SEO for all the others
            for (let i = 1; i < paragraphs.length; i++) {
              paragraphs[i].classList.add('hidden');
            }
            return doc.body.innerHTML;
          };

          return (
            <li
              key={`${editor.id}`}
              className="md:flex md:justify-between items-center first:mt-0 last:mb-0 list-none md:mb-6 mb-3"
            >
              {editor.firstName ? (
                <div className="items-start grid grid-flow-col auto-row-max justify-start mt-3 md:mt-6">
                  {editor.relationships?.field_graduate_image ? (
                    <div className="h-full flex items-start mr-6">
                      <Link to={`${editor.path.alias}`} data-cy={`id:${editor.id}`}>
                        <GatsbyImage
                          className="rounded-full border-4 border-white shadow-sm"
                          imgStyle={{ borderRadius: '100%' }}
                          image={
                            editor.relationships?.field_graduate_image?.localFile?.childImageSharp?.gatsbyImageData
                          }
                          alt={`${editor.field_graduate_image?.alt}`}
                        />
                      </Link>
                    </div>
                  ) : (
                    <Link to={`${editor.path.alias}`} data-cy={`id:${editor.id}`}>
                      {getContributorPlaceholderLogo(
                        editor.firstName,
                        editor.lastName,
                        'mr-6 w-28 h-28 text-4xl font-semibold'
                      )}
                    </Link>
                  )}
                  <div className="flex flex-col h-full overflow-hidden">
                    <div className="text-left">
                      {editor.firstName && (
                        <Link to={`${editor.path.alias}`} data-cy={`id:${editor.id}`}>
                          <p className="text-lg font-bold leading-7">
                            {editor.firstName} {editor.lastName}
                          </p>
                        </Link>
                      )}
                      <div className="flex items-center flex-row mb-1">
                        {editorialTeam && (
                          <div className="mr-2 h-7 flex items-center">{siteLogoLink(data, '', 'h-5', true)}</div>
                        )}
                        {editor.field_job_title && (
                          <p className="text-base font-medium leading-7">{editor.field_job_title}</p>
                        )}
                      </div>
                    </div>
                    {editor.description && (
                      <div
                        className="text-left text-base font-normal leading-5 line-clamp-[9] md:line-clamp-2"
                        dangerouslySetInnerHTML={{
                          __html: editor.description && ShowFirstParagraph(editor.description)
                        }}
                      ></div>
                    )}
                  </div>
                </div>
              ) : (
                <div>&nbsp;</div>
              )}
            </li>
          );
        })}
      </div>
    );
  };

  const paginatedTeam = (teamData: any, team: string) => {
    const isEditorialTeam = team === 'editorialTeam';
    const itemsPerPage = 12;
    const currentPage = isEditorialTeam ? editorialTeam.currentPage : guestTeam.currentPage;
    const setCurrentPage = isEditorialTeam ? setEditorialTeam : setGuestTeam;
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPageData = teamData.slice(startIndex, endIndex);

    const handlePageChange = (selectedPage: number) => {
      setCurrentPage((prevState: any) => ({
        ...prevState,
        currentPage: selectedPage - 1
      }));
    };

    return (
      <>
        {/* Render team members for the current page */}
        {getContributors(currentPageData)}

        <div className="flex justify-center mt-7">
          <PaginationControls
            onPageChange={handlePageChange}
            totalCount={teamData.length}
            currentPage={currentPage + 1}
            pageSize={12}
          />
        </div>
      </>
    );
  };

  return (
    <Layout meta={meta}>
      <main>
        <SectionOneCol bkgColor={'bg-transparent'} indent={false} className="md:pt-10 pt-2">
          <a id="EditorialTeam" />
          <h1 className="text-2xl font-bold mb-4">Meet the {data.site.siteMetadata.brandName} editors</h1>
          <p className="text-small font-normal text-left mb-4 md:mb-10">
            We’re the experts behind {data.site.siteMetadata.brandName}, the careers platform designed to help you make
            the best decisions about your future. We’re here to connect you with the latest jobs, placements and tips
            and tricks to get hired. We’ve collectively accrued decades of experience in bringing you thoughtful and
            in-depth careers advice, alongside exclusive insights from recruiters, professionals and careers advisers.
            Click on our bios to find out more.
          </p>
          <hr className="md:invisible" />
          {/* Only use paginated team rendering */}
          {paginatedTeam(editorialTeamData, 'editorialTeam')}
          <hr className="md:invisible mb-20 mt-3 md:mt-0" />
          {guestTeamData.length > 0 ? (
            <React.Fragment>
              <a id="GuestTeam" />
              <h2 className="text-2xl font-bold mb-4">Guest editors</h2>
              <p className="text-small font-normal text-left mb-10">
                From time to time, we are joined by special guest writers – editors handpicked for their expertise in
                careers content or communications. Click on their bios to find out more.
              </p>
              <hr className="md:invisible" />
              {paginatedTeam(guestTeamData, 'guestTeam')}
              <hr className="invisible mb-10 md:mb-[60]" />
            </React.Fragment>
          ) : (
            <div>&nbsp;</div>
          )}
        </SectionOneCol>
      </main>
    </Layout>
  );
};

export default withLocation(withDataTransformer(EditorialTeamTemplate));

export const query = graphql`
  query {
    editorialTeam: allContributor(filter: { field_editorial_team: { eq: "1" } }, sort: { title: ASC }) {
      nodes {
        id
        uuid: drupal_id
        nid: drupal_internal__nid
        path {
          alias
        }
        description: field_abstract
        firstName: field_contributor_first_name
        lastName: field_contributor_last_name
        field_job_title
        relationships {
          field_graduate_image {
            localFile {
              childImageSharp {
                ...ProfileImage
              }
            }
          }
        }
      }
    }
    guestEditors: allContributor(filter: { field_editorial_team: { eq: "0" } }, sort: { title: ASC }) {
      nodes {
        id
        uuid: drupal_id
        nid: drupal_internal__nid
        path {
          alias
        }
        description: field_abstract
        firstName: field_contributor_first_name
        lastName: field_contributor_last_name
        field_job_title
        relationships {
          field_graduate_image {
            localFile {
              childImageSharp {
                ...ProfileImage
              }
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        brandName
      }
    }
  }
`;
